import { FacebookOutlined, InstagramOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { integrationsApi } from '../integrations.api';

const fmt = (n: number | null): string =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat().format(n);

const money = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * Read-only insights for the connected Meta account: Facebook Page + linked
 * Instagram engagement and ad spend. Each section renders whatever the granted
 * scopes expose; sections stay empty (rather than error) when a scope is missing
 * or still pending Meta App Review.
 */
export function MetaInsightsPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const query = useQuery({
    queryKey: ['meta-insights', orgId, projectId],
    queryFn: () => integrationsApi.metaInsights(orgId, projectId),
    staleTime: 60_000,
  });

  const data = query.data;

  return (
    <Card
      size="small"
      title="Meta insights"
      loading={query.isLoading}
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Refresh
        </Button>
      }
    >
      {data && !data.pageSelected ? (
        <Alert type="info" showIcon message="Pick a Facebook Page above to see its insights." />
      ) : null}

      {data && !data.facebook && !data.instagram && !data.ads && data.pageSelected ? (
        <Alert
          type="warning"
          message="No insights available yet"
          description="This usually means the read_insights, instagram_manage_insights, or ads_read permissions haven't been approved for the Meta app yet. They require Meta App Review before returning data in production."
        />
      ) : null}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {data?.facebook ? (
          <div>
            <Typography.Text strong>
              <FacebookOutlined style={{ color: '#1877F2', marginInlineEnd: 6 }} />
              {data.facebook.pageName}
            </Typography.Text>
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col xs={12} sm={6}>
                <Statistic title="Followers" value={fmt(data.facebook.followers)} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Posts" value={fmt(data.facebook.postCount)} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Reach (28d)" value={fmt(data.facebook.impressions28d)} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Engagements (28d)" value={fmt(data.facebook.engagements28d)} />
              </Col>
            </Row>
            {data.facebook.recentPosts.length > 0 ? (
              <List
                size="small"
                style={{ marginTop: 8 }}
                header={<Typography.Text type="secondary">Recent posts</Typography.Text>}
                dataSource={data.facebook.recentPosts}
                renderItem={(p) => (
                  <List.Item>
                    <Typography.Text ellipsis style={{ maxWidth: '100%' }}>
                      {p.url ? (
                        <a href={p.url} target="_blank" rel="noreferrer">
                          {p.caption || '(no caption)'}
                        </a>
                      ) : (
                        p.caption || '(no caption)'
                      )}
                    </Typography.Text>
                  </List.Item>
                )}
              />
            ) : null}
          </div>
        ) : null}

        {data?.instagram ? (
          <div>
            <Typography.Text strong>
              <InstagramOutlined style={{ color: '#C13584', marginInlineEnd: 6 }} />@
              {data.instagram.username}
            </Typography.Text>
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col xs={12} sm={6}>
                <Statistic title="Followers" value={fmt(data.instagram.followers)} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic title="Media" value={fmt(data.instagram.mediaCount)} />
              </Col>
            </Row>
          </div>
        ) : null}

        {data?.ads ? (
          <div>
            <Typography.Text strong>Ads</Typography.Text>
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col xs={12} sm={6}>
                <Statistic title="Active campaigns" value={fmt(data.ads.activeCampaigns)} />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Spend (30d)"
                  value={money(data.ads.spend30d, data.ads.currency)}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Lifetime spend"
                  value={money(data.ads.lifetimeSpend, data.ads.currency)}
                />
              </Col>
            </Row>
            {data.ads.campaigns.length > 0 ? (
              <List
                size="small"
                style={{ marginTop: 8 }}
                header={<Typography.Text type="secondary">Campaigns</Typography.Text>}
                dataSource={data.ads.campaigns}
                renderItem={(c) => (
                  <List.Item>
                    <Space>
                      <Tag color={c.effectiveStatus === 'ACTIVE' ? 'green' : 'default'}>
                        {c.effectiveStatus}
                      </Tag>
                      <Typography.Text>{c.name}</Typography.Text>
                    </Space>
                  </List.Item>
                )}
              />
            ) : null}
          </div>
        ) : null}

        {data && data.pageSelected && !data.facebook && !data.instagram && !data.ads ? (
          <Empty description="Nothing to show yet" />
        ) : null}
      </Space>
    </Card>
  );
}
